import { useCallback, useMemo, useRef, useState } from 'react';

import { FileStatus } from '@libs/constants';
import { type FileUploadType, type UploadedDocumentRef } from '@libs/types';

import { useChatDocumentUpload } from './useChatDocumentUpload';

export interface UseChatAttachmentsParams {
  /**
   * Where uploads are filed. On the new-chat screen this is a draft id, since
   * the real conversation does not exist until the first message is sent.
   */
  conversationId: string;
}

export interface ChatAttachments {
  files: FileUploadType[];
  /** The finished upload as a message document ref — undefined until one is ready. */
  document?: UploadedDocumentRef;
  addFiles: (files: File[]) => Promise<void>;
  removeFile: (fileId: string) => void;
  cancelFile: (fileId: string) => void;
  clear: () => void;
  reportValidationError: (error: {
    type: 'size' | 'type' | 'duplicate' | 'maxFiles';
    fileName: string;
    fileExtension?: string;
    maxFiles?: number;
  }) => void;
}

/**
 * Staging area for the document that rides along with the next message.
 *
 * Owns the upload lifecycle and the shape the composer renders, so both places
 * a composer appears — the new-chat hero and an open thread — attach documents
 * the same way instead of each maintaining its own copy of the flow.
 */
export const useChatAttachments = ({
  conversationId,
}: UseChatAttachmentsParams): ChatAttachments => {
  const { uploadDocument, cancelUpload } = useChatDocumentUpload({ conversationId });
  const [files, setFiles] = useState<FileUploadType[]>([]);
  // A ref, not state: the guard has to be read and set within one synchronous
  // call, before React has re-rendered with the new file.
  const isUploadingRef = useRef(false);

  const addFiles = useCallback(
    async (selected: File[]) => {
      const file = selected[0]; // One at a time — a message carries one document.
      // The attach button is already disabled mid-upload; this keeps the hook
      // safe for any caller, since a second run would drop the first file's
      // state while its request was still in flight.
      if (!file || isUploadingRef.current) return;

      isUploadingRef.current = true;
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      setFiles([
        {
          id: tempId,
          fileName: file.name,
          size: file.size,
          status: FileStatus.UPLOADING,
          progress: 0,
          uploadedAt: new Date().toISOString(),
          mimeType: file.type,
        },
      ]);

      try {
        await uploadDocument(file, {
          onProgress: (progress) => {
            setFiles((prev) => prev.map((f) => (f.id === tempId ? { ...f, progress } : f)));
          },
          onSuccess: (documentId, uploadMeta) => {
            setFiles((prev) =>
              prev.map((f) =>
                f.id === tempId
                  ? {
                      ...f,
                      id: documentId,
                      status: FileStatus.UPLOADED,
                      progress: 100,
                      s3Key: uploadMeta?.s3Key,
                      mimeType: uploadMeta?.mimeType ?? file.type,
                    }
                  : f,
              ),
            );
          },
          onError: (error) => {
            console.error('[useChatAttachments] Upload failed:', error);
            setFiles((prev) =>
              prev.map((f) => (f.id === tempId ? { ...f, status: FileStatus.FAILED, error } : f)),
            );
          },
        });
      } finally {
        isUploadingRef.current = false;
      }
    },
    [uploadDocument],
  );

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const cancelFile = useCallback(
    (fileId: string) => {
      cancelUpload(fileId);
      removeFile(fileId);
    },
    [cancelUpload, removeFile],
  );

  const clear = useCallback(() => setFiles([]), []);

  const reportValidationError = useCallback(
    (error: { type: string; fileName: string }) => {
      console.error('[useChatAttachments] Validation failed:', error);
    },
    [],
  );

  const document = useMemo<UploadedDocumentRef | undefined>(() => {
    const uploaded = files.find((f) => f.status === FileStatus.UPLOADED);
    if (!uploaded?.s3Key) return undefined;

    return {
      s3Key: uploaded.s3Key,
      docId: uploaded.id,
      fileName: uploaded.fileName,
      mimeType: uploaded.mimeType,
      fileSize: uploaded.size,
    };
  }, [files]);

  // Memoised so the returned object is referentially stable between renders.
  // Consumers put it straight into `useCallback` dependency arrays, and a fresh
  // object each render would rebuild their handlers — and every prop below them.
  return useMemo(
    () => ({ files, document, addFiles, removeFile, cancelFile, clear, reportValidationError }),
    [files, document, addFiles, removeFile, cancelFile, clear, reportValidationError],
  );
};
